'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, FlagSolid, HomeSolid } from '@byway/icons';
import { DialogTrigger } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Popover } from '~/components/Popover';
import { TabsContainer } from '~/components/Tabs';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { usePathname, useRouter } from '~/i18n/navigation';
import { type CreateTripTab, usePlaygroundActions, usePlaygroundContextParams, usePlaygroundEndPlace, usePlaygroundOrigin, usePlaygroundUIState } from '~/stores/playgroundStore';
import { PlaygroundStartEndPanel, PlaygroundStartEndTabs } from '../PlaygroundStartEndTabs/PlaygroundStartEndTabs';
import { PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH, PRIMARY_EDIT_BAR_POPOVERS_OFFSET } from './helpers';
import styles from './OriginButton.module.css';
interface OriginButtonProps {
  buttonVariant: 'pill' | 'tertiary';
}

/**
 * Desktop origin button with popover
 */
export function OriginButton(t0) {
  const $ = _c(30);
  const {
    buttonVariant
  } = t0;
  const pathname = usePathname();
  const isDesktop = useIsDesktop();
  const {
    isDrawerOpen,
    drawerItem,
    createTripTab
  } = usePlaygroundUIState();
  const {
    closeDrawer,
    setCreateTripTab,
    openDrawer
  } = usePlaygroundActions();
  const {
    chosenOrigin
  } = usePlaygroundContextParams();
  const router = useRouter();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReview = t1;
  const isPopoverOpen = Boolean(isDesktop && isDrawerOpen && drawerItem === "start-end");
  let t2;
  if ($[2] !== isReview || $[3] !== openDrawer || $[4] !== router) {
    t2 = () => {
      if (isReview) {
        router.back();
      }
      openDrawer("start-end");
    };
    $[2] = isReview;
    $[3] = openDrawer;
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handlePress = t2;
  let t3;
  if ($[6] !== closeDrawer) {
    t3 = isOpen => {
      if (!isOpen) {
        closeDrawer();
      }
    };
    $[6] = closeDrawer;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleOpenChange = t3;
  const t4 = !!chosenOrigin;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ButtonContent />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== buttonVariant || $[10] !== handlePress || $[11] !== t4) {
    t6 = <Button variant={buttonVariant} size="extraSmall" onPress={handlePress} isPressed={t4}>{t5}</Button>;
    $[9] = buttonVariant;
    $[10] = handlePress;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== setCreateTripTab) {
    t7 = key => {
      setCreateTripTab(key as CreateTripTab);
    };
    $[13] = setCreateTripTab;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PlaygroundStartEndTabs />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== createTripTab) {
    t9 = <PlaygroundStartEndPanel isHeroCta={false} variant="module" travelType={createTripTab} />;
    $[16] = createTripTab;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== createTripTab || $[19] !== t7 || $[20] !== t9) {
    t10 = <TabsContainer selectedKey={createTripTab} onSelectionChange={t7}>{t8}{t9}</TabsContainer>;
    $[18] = createTripTab;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleOpenChange || $[23] !== t10) {
    t11 = <Popover ariaLabel="Edit origin and end place" maxWidth={PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH} noHeader={true} offset={PRIMARY_EDIT_BAR_POPOVERS_OFFSET} placement="bottom" isDismissable={true} onOpenChange={handleOpenChange} close={true}>{t10}</Popover>;
    $[22] = handleOpenChange;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== handleOpenChange || $[26] !== isPopoverOpen || $[27] !== t11 || $[28] !== t6) {
    t12 = <DialogTrigger isOpen={isPopoverOpen} onOpenChange={handleOpenChange}>{t6}{t11}</DialogTrigger>;
    $[25] = handleOpenChange;
    $[26] = isPopoverOpen;
    $[27] = t11;
    $[28] = t6;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}

/**
 * Mobile origin button (drawer trigger)
 */
export function OriginButtonMobile(t0) {
  const $ = _c(11);
  const {
    buttonVariant
  } = t0;
  const pathname = usePathname();
  const {
    openDrawer
  } = usePlaygroundActions();
  const {
    chosenOrigin
  } = usePlaygroundContextParams();
  const router = useRouter();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReview = t1;
  let t2;
  if ($[2] !== isReview || $[3] !== openDrawer || $[4] !== router) {
    t2 = () => {
      if (isReview) {
        router.back();
        return;
      }
      openDrawer("start-end");
    };
    $[2] = isReview;
    $[3] = openDrawer;
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handlePress = t2;
  const t3 = !!chosenOrigin;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ButtonContent />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== buttonVariant || $[8] !== handlePress || $[9] !== t3) {
    t5 = <Button onPress={handlePress} variant={buttonVariant} size="extraSmall" isPressed={t3}>{t4}</Button>;
    $[7] = buttonVariant;
    $[8] = handlePress;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function ButtonContent() {
  const $ = _c(10);
  const {
    chosenDestination
  } = usePlaygroundContextParams();
  const {
    dpOrigin,
    cmsOrigin
  } = usePlaygroundOrigin();
  const {
    dpEndPlace,
    cmsEndPlace
  } = usePlaygroundEndPlace();
  const isRoundTrip = !chosenDestination || chosenDestination.id === dpOrigin?.id;
  const originName = cmsOrigin?.placeName || dpOrigin?.n;
  const destinationName = cmsEndPlace?.placeName || dpEndPlace?.n;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HomeSolid decorative={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== originName) {
    t1 = <div className={styles.buttonGroup}>{t0}<span>{originName}</span></div>;
    $[1] = originName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== chosenDestination || $[4] !== destinationName || $[5] !== isRoundTrip) {
    t2 = !isRoundTrip && chosenDestination && <><div className={styles.arrowCircle}><ArrowRight color="var(--by-color-icon-default)" strokeWidth={2} decorative={true} /></div><div className={styles.buttonGroup}><FlagSolid decorative={true} /><span>{destinationName}</span></div></>;
    $[3] = chosenDestination;
    $[4] = destinationName;
    $[5] = isRoundTrip;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className={styles.buttonContent}>{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}